import * as React from 'react'
import classnames from 'classnames'
import { useInView } from 'react-intersection-observer'
import { getOptimizedImage } from './utils'
import { logger, metrics } from 'lib/slardar-utils'
import ImageStyles from './style'
export { default as ImageZoom } from './image-zoom'

// 常用的一些图片
export const userLoading = require('static/user-loading.png')
export const carLoading = require('static/car-bg-l.png')
export const logoLoading = require('static/dongchedi-logo.svg')

export type ImageProps = {
  /**
   * 图片宽度
   */
  width: number
  /**
   * 图片高度
   */
  height: number
  /**
   * 图片资源地址
   */
  src: string
  /**
   * className
   */
  className?: string
  /**
   * alt
   */
  alt?: string
  /**
   * 是否懒加载
   */
  lazy?: boolean
  /**
   * 不使用图片格式优化，主要是webp
   * @default false
   */
  unoptimized?: boolean
  /**
   * 是否开启加载完毕后的动画，默认开启
   */
  useTransition?: boolean
  /**
   * hover动画
   */
  hoverTransition?: boolean
  /**
   * intersection observer的rootMargin
   * @default 0px 100px
   */
  rootMargin?: string
  /**
   * 自定义图片的行内样式
   */
  style?: React.CSSProperties
  /**
   * 自定义默认图片
   */
  cusDefaultImg?: string
  /**
   * 自定义错误图片
   */
  errorImg?: string
  /**
   * 是否自动适配 设备像素比 服务器端默认2倍，客户端根据实际的devicePixelRatio
   */
  autoPixelRatio?: boolean
}

type ImgElementStyle = NonNullable<JSX.IntrinsicElements['img']['style']>

const classNamePrefix = 'motor-pc-image'

const defaultImg =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwMCIgaGVpZ2h0PSIxMDAwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZlcnNpb249IjEuMSIvPg=='

export const Image: React.FC<ImageProps> = props => {
  const {
    src,
    style,
    lazy = false,
    className,
    rootMargin = '500px 100px',
    unoptimized = false,
    useTransition = true,
    hoverTransition = false,
    width,
    height,
    alt = '',
    cusDefaultImg = defaultImg,
    errorImg,
    autoPixelRatio = false,
  } = props

  const isBase64 = src?.startsWith?.('data:')
  // 为data：时，不进行预加载和优化
  const isLazy = isBase64 ? false : lazy

  const notUseWebp = isBase64 ? true : unoptimized

  const [setRef, isIntersected] = useInView({
    rootMargin,
    threshold: 0,
    triggerOnce: true,
  })
  const inView = !isLazy || isIntersected

  let imgStyle: ImgElementStyle | undefined = {
    visibility: inView ? 'inherit' : 'hidden',
  }

  const realSrc = React.useMemo(() => {
    if (!inView) {
      // 不可见时，设置一个透明的背景图
      return cusDefaultImg
    }
    return getOptimizedImage({
      src,
      unoptimized: notUseWebp,
      width,
      height,
      autoPixelRatio,
    })
  }, [inView, src, width, height, notUseWebp, autoPixelRatio])

  const imgRef = React.useRef<HTMLImageElement | null>(null)

  // 图片加载错误重试的次数
  const retryTimesRef = React.useRef(0)

  if (unoptimized) {
    imgStyle = undefined
  }

  const setRefCallbck = React.useCallback(
    ele => {
      imgRef.current = ele
      setRef(ele)
    },
    [setRef, imgRef]
  )
  React.useEffect(() => {
    retryTimesRef.current = 0
  }, [src, width, height, notUseWebp])
  return (
    <img
      ref={setRefCallbck}
      alt={alt}
      src={realSrc}
      style={{
        ...style,
        ...imgStyle,
      }}
      className={classnames(
        className,
        hoverTransition && `${classNamePrefix}-hover`,
        useTransition && `${classNamePrefix}-fade`,
        inView && `${classNamePrefix}-in`
      )}
      onError={() => {
        logger.error(
          `[motor-fe/pc/image onError] error message: src=${realSrc}`,
          {
            src,
          }
        )
        metrics.counter('motor-fe/pc.image.onerror', { realSrc, src })
        if (retryTimesRef.current > 0) {
          /* eslint-disable no-unused-expressions */
          imgRef.current?.setAttribute('src', errorImg || cusDefaultImg)
          return
        }
        retryTimesRef.current += 1
        if (src === realSrc) {
          /* eslint-disable no-unused-expressions */
          imgRef.current?.setAttribute('src', errorImg || cusDefaultImg)
          return
        }
        /* eslint-disable no-unused-expressions */
        imgRef.current?.setAttribute('src', src)
      }}
    />
  )
}

export { ImageStyles }
